import { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import loadable from "@loadable/component";
import CategoryHeader from "../../components/CategoryHeader";
import ProductListingPage from "../../components/ProductListingPage";
import ProductListSkeleton from "../../components/Results/ProductListSkeleton";
import { useBreadcrumbUpdates } from "../../components/hooks";
import ClientOnly from "../../components/ClientOnly";

const ErrorMessage = loadable(
  () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "CATEGORY_FOOTER" */ "../../components/ErrorMessage"
    ),
  { fallback: null },
);

const CategoryOutOfStock = loadable(
  () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "CATEGORY_FOOTER" */ "../../components/CategoryOutOfStock"
    ),
  { fallback: null },
);

const Pagination = loadable(
  () =>
    import(
      /* webpackPrefetch: true, webpackChunkName: "CATEGORY_FOOTER" */ "../../components/Pagination"
    ),
  { fallback: null },
);

const LoyaltyOptInModal = loadable(
  () =>
    import(
      /* webpackChunkName: "LOYALTY_OPT_IN_MODAL" */ "../../components/LoyaltyOptInModal"
    ),
  { fallback: null },
);

const SuccessMessage = loadable(
  () =>
    import(
      /* webpackChunkName: "CATEGORY_FOOTER" */ "../../components/SuccessMessage"
    ),
  {
    fallback: null,
    resolveComponent: (mod) => mod.SuccessMessage,
  },
);

const CategoryPage = ({
  categoryError,
  itemCount,
  hasFacets,
  isLoading,
  title,
  pageNumber,
  breadcrumbs,
  topContent = null,
  hideHeaderDescription = false,
  ...rest
}) => {
  const facetsRef = useRef(null);

  useBreadcrumbUpdates(breadcrumbs);
  useEffect(() => {
    if (title && pageNumber === 1) {
      document.title = title;
    }
  }, [title]);

  const handleScroll = useCallback(() => {
    facetsRef.current?.base?.scrollIntoView({ behavior: "smooth" });
  }, [facetsRef]);

  if (isLoading) {
    return <ProductListSkeleton />;
  }

  if (categoryError) {
    return <ErrorMessage {...categoryError} />;
  }

  if (itemCount === 0 && !hasFacets) {
    return <CategoryOutOfStock {...rest} />;
  }

  return (
    <div>
      <CategoryHeader
        scrollFunction={handleScroll}
        hideDescription={hideHeaderDescription}
      />
      {topContent}
      <ProductListingPage ref={facetsRef} {...rest} />
      <Pagination />
      <ClientOnly>
        <LoyaltyOptInModal {...rest} />
        <SuccessMessage />
      </ClientOnly>
    </div>
  );
};

CategoryPage.propTypes = {
  categoryError: PropTypes.shape({
    status: PropTypes.number,
  }),
  hasFacets: PropTypes.bool.isRequired,
  itemCount: PropTypes.number.isRequired,
  isLoading: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  pageNumber: PropTypes.number,
  breadcrumbs: PropTypes.array.isRequired,
  topContent: PropTypes.node,
  hideHeaderDescription: PropTypes.bool,
};

CategoryPage.defaultProps = {};

export default CategoryPage;
